import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  useAddProductLicenseMutation,
  useFetchSingleProduct,
  useFetchSingleProductLicense,
  useUpdateProductLicenseMutation
} from "../../../hooks/product-hooks";
import { ProductFeature, ProductLicense } from "../../../types/product";
import { asArray } from "../../../support/utils";
import { useAttachedItemsUpdateMutation } from "../../../hooks/gtm-solution-hooks";
import { CacheKey } from "../../../types/cache-keys";
import AdminConfirmDialog from "../Common/AdminConfirmDialog";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  productId: number;
  licenseId?: number;
  onClose: () => void;
  onSaved: () => void;

  /** NEW: view-only mode */
  readOnly?: boolean;
};

export default function ProductLicenseEditorDialog({
                                                     open,
                                                     mode,
                                                     productId,
                                                     licenseId,
                                                     onClose,
                                                     onSaved,
                                                     readOnly = false,
                                                   }: Props) {
  const isEdit = mode === "edit" && !!licenseId;

  const { data: product, isLoading: productLoading } = useFetchSingleProduct(productId);
  const { data: licenseData, isLoading: licenseLoading } = useFetchSingleProductLicense(productId, licenseId);

  const license: ProductLicense = useMemo(() => {
    if (isEdit) {
      if (productLoading && licenseLoading) return null;
      if (licenseData) return licenseData;
      const found = asArray(product?.license).find((s) => s.id === licenseId);
      return found ?? null;
    }
    return null;
  }, [isEdit, licenseId, product, productLoading, licenseData, licenseLoading]);

  const { mutateAsync: addLicense, isPending: addingLicense } = useAddProductLicenseMutation(productId);
  const { mutateAsync: updateLicense, isPending: updatingLicense } = useUpdateProductLicenseMutation(productId);
  const { mutateAsync: updateJoins, isPending: updatingJoins } = useAttachedItemsUpdateMutation<ProductLicense, ProductFeature>({
    type: CacheKey.ProductLicenseFeature,
    productId
  });

  const featureOptions = useMemo(() => {
    return product?.feature ?? [];
  }, [product?.feature]);
  const [licenseFeatures, setLicenseFeatures] = useState<ProductFeature[]>([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<ProductFeature["id"][]>([]);
  const [initialSelectedFeatureIds, setInitialSelectedFeatureIds] = useState<ProductFeature["id"][]>([]);

  const [form, setForm] = useState<any>({ name: "", consumption_model: "" });
  const [initialForm, setInitialForm] = useState<any>({ name: "", consumption_model: "" });
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    const featuresForLicense = featureOptions.filter(
      (feature) => feature.license?.some((l: any) => l.id === licenseId)
    );
    setLicenseFeatures(featuresForLicense);
  }, [featureOptions, licenseId, isEdit]);

  useEffect(() => {
    if (!open) return;

    if (!isEdit || !license) {
      const emptyForm = {
        name: "",
        consumption_model: ""
      };
      setForm(emptyForm);
      setInitialForm(emptyForm);
      setSelectedFeatureIds([]);
      setInitialSelectedFeatureIds([]);
      return;
    }

    const nextForm = {
      name: license.name ?? "",
      consumption_model: license.consumption_model ?? "",
    };

    const nextSelectedFeatureIds = licenseFeatures.map((f) => f.id);

    setForm(nextForm);
    setInitialForm(nextForm);
    setSelectedFeatureIds(nextSelectedFeatureIds);
    setInitialSelectedFeatureIds(nextSelectedFeatureIds);
  }, [open, isEdit, license, licenseFeatures, mode]);

  const saving =
    addingLicense ||
    updatingLicense ||
    updatingJoins;

  const disabledAll = saving || readOnly;

  const hasUnsavedChanges = useMemo(() => {
    const selectedNow = [...(selectedFeatureIds ?? [])].sort((a, b) => a - b);
    const selectedInitial = [...(initialSelectedFeatureIds ?? [])].sort((a, b) => a - b);

    return (
      form.name !== initialForm.name ||
      form.consumption_model !== initialForm.consumption_model ||
      JSON.stringify(selectedNow) !== JSON.stringify(selectedInitial)
    );
  }, [form, initialForm, selectedFeatureIds, initialSelectedFeatureIds]);

  const resetAndClose = () => {
    setForm({ name: "", consumption_model: "" });
    setInitialForm({ name: "", consumption_model: "" });
    setSelectedFeatureIds([]);
    setInitialSelectedFeatureIds([]);
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleClose = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    resetAndClose();
  };

  const handleSave = async () => {
    if (readOnly) {
      handleClose();
      return;
    }

    let finalLicenseId = null;

    if (mode === "create") {
      const created: any = await addLicense({ ...form });
      finalLicenseId = created?.id;
    } else if (isEdit && licenseId) {
      await updateLicense({ ...form, id: licenseId });
      finalLicenseId = licenseId;
    }

    if (finalLicenseId) {
      const ids = new Set(selectedFeatureIds ?? []);
      await updateJoins({ selectedItems: ids, attachingToId: finalLicenseId });
    }

    onSaved();
    resetAndClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {readOnly
            ? mode === "create"
              ? "View License"
              : `View License: ${form?.name || `#${licenseId}`}`
            : mode === "create"
              ? "Create License"
              : `Edit License: ${form?.name || `#${licenseId}`}`}
        </DialogTitle>

        <DialogContent>
          {(productLoading || licenseLoading || saving) && (
            <Box display="flex" gap={2} alignItems="center" py={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading…</Typography>
            </Box>
          )}

          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={form.name ?? ""}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, name: e.target.value }))}
            disabled={disabledAll}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Consumption Model"
            value={form.consumption_model ?? ""}
            onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, consumption_model: e.target.value }))}
            disabled={disabledAll}
          />

          <Box mt={2}>
            <FormControl fullWidth disabled={disabledAll}>
              <InputLabel id="license-features-label">Supported Features (this product)</InputLabel>
              <Select
                labelId="license-features-label"
                multiple
                value={selectedFeatureIds ?? []}
                label="Supported Features (this product)"
                renderValue={(selected) =>
                  (selected as number[])
                    .map((id) => featureOptions.find((f: any) => f.id === id)?.name || `#${id}`)
                    .join(", ")
                }
                onChange={(e) =>
                  !readOnly &&
                  setSelectedFeatureIds(() => {
                    const selectedIds = new Set(e.target.value as number[]);
                    return featureOptions
                      .filter((f: any) => selectedIds.has(f.id))
                      .map((f) => f.id);
                  })
                }
              >
                {featureOptions.map((f: any) => (
                  <MenuItem key={f.id} value={f.id}>
                    <Checkbox checked={(selectedFeatureIds ?? []).includes(f.id)} />
                    <ListItemText primary={f.name} secondary={f.description || ""} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Deleting licenses is disabled in the Product editor; use this dialog to manage feature coverage.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You have unsaved changes in this license editor.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}